import { API_BASE_URL } from "../../../config/api";
import { getToken } from "../storage/authStorage";
import { ActiveLocation } from "../../hooks/useActiveLocations";

/**
 * ZOUR-65 R17 — the active locations, for code that is not a React component.
 *
 * `useActiveLocations` covers the pickers. The validation clients need the same
 * list from inside a plain async function, to answer one question before they
 * post: WHICH STATE IS OUR SIDE OF THIS SUPPLY? Sending the company row's state
 * there is what raised "Source of supply does not match company location" on a
 * perfectly correct Bengaluru invoice — the company is in Tamil Nadu, the
 * invoice was issued from Karnataka, and core compared the two.
 *
 * Cached for the life of the page. Locations change when somebody adds a GST
 * registration in Zoho and syncs, which is not something that happens between
 * two validations of the same document.
 *
 * FAILS QUIET, like the hook: an empty list means every caller falls back to
 * exactly what it sent before ZOUR-65.
 */
let cached: Promise<ActiveLocation[]> | null = null;

export const getActiveLocations = (): Promise<ActiveLocation[]> => {
  if (cached) {
    return cached;
  }

  cached = (async (): Promise<ActiveLocation[]> => {
    try {
      const token = getToken();
      if (!token) {
        return [];
      }

      const response = await fetch(`${API_BASE_URL}/api/v1/locations/active`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      const body = await response.json();

      if (response.ok && body?.success && body?.data) {
        return (body.data.locations ?? []) as ActiveLocation[];
      }
    } catch {
      // Deliberately quiet — see the comment above.
    }

    // Do not cache a failure: the next validation gets a fresh attempt.
    cached = null;

    return [];
  })();

  return cached;
};

/**
 * OUR state code for a document, from the registration it is filed against.
 *
 * The same order core uses in `getDocumentLocation()`, so the screen and the
 * server answer the same question the same way:
 *   1. the reviewer's pick / the stored `location_id`
 *   2. the pipeline's `location_match`
 *   3. the primary — where Zoho files a document that names no location
 *
 * NULL when nothing can answer: no locations loaded, or the row has no state
 * code. Callers then send what they always sent.
 */
export const ourSupplyStateFor = async (documentJson: {
  location_id?: string | null;
  location_match?: Record<string, unknown> | null;
}): Promise<string | null> => {
  const locations = await getActiveLocations();
  if (locations.length === 0) {
    return null;
  }

  const chosenId = documentJson.location_id ?? null;
  const matchedId = (documentJson.location_match?.location_id as string | undefined) ?? null;

  const location =
    locations.find((candidate) => chosenId && candidate.zoho_location_id === chosenId) ??
    locations.find((candidate) => matchedId && candidate.zoho_location_id === matchedId) ??
    locations.find((candidate) => candidate.is_primary) ??
    null;

  const code = location?.state_code?.trim() ?? "";

  return code === "" ? null : code;
};
